import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from 'store/index';
import {
  AlphaVoiceStatus,
  IAlphaMessage,
  IAlphaPendingConfirmation,
  IAlphaPodState,
  IAlphaRequestInFlight,
  IAlphaToolCallStatus,
  IAlphaVoiceResult,
} from 'store/alpha/types';
import { CardServerError } from 'components/Alpha/cards/cardTypes';
import { AlphaEvent, IAlphaResolvedContextResult } from 'services/alpha/types';
import {
  ThunkCreateAlphaSession,
  ThunkGetAlphaSessionsForContext,
  ThunkRefreshAfterVoice,
  ThunkReloadAlphaMessages,
  ThunkRestorePendingConfirmations,
  ThunkSelectAlphaSession,
} from 'store/alpha/thunks';
import { clearBackedExpiryNotices, toAlphaCard } from 'services/alpha/pendingConfirmations';
import { isLocalOnly, mergeReload, noticeAnchor } from 'services/alpha/reloadMerge';
import { toVisual } from 'services/alpha/visuals';
import { IOaiRealtimeEvent } from 'services/alpha/oaiRealtimeEvents';
import { CANCELLED_ITEM_PREFIX, applyVoiceTranscriptEvent } from 'services/alpha/voiceTranscript';
import { VoiceErrorCode } from 'services/alpha/voiceErrors';

type ConfirmationRequiredEvent = Extract<AlphaEvent, { type: 'confirmation_required' }>;
type CardRequiredEvent = Extract<AlphaEvent, { type: 'card_required' }>;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toPendingCard = (event: CardRequiredEvent): IAlphaPendingConfirmation => {
  const card = toAlphaCard(event.card);
  const preview = card?.preview;
  return {
    toolName: event.toolName,
    preview: isPlainObject(preview) ? preview : {},
    token: event.token,
    kind: event.kind,
    ...(card ? { card } : {}),
    ...(event.title ? { title: event.title } : {}),
  };
};

const nextSelection = (state: IAlphaPodState): number => (state.selection ?? 0) + 1;

const withoutToken = (tokens: string[] | undefined, token: string): string[] =>
  (tokens ?? []).filter(item => item !== token);

const clearCardMarkers = (state: IAlphaPodState): void => {
  state.submittingTokens = [];
  state.cancellingTokens = [];
};

const changeSession = (state: IAlphaPodState, sessionId: number | undefined): void => {
  if (state.sessionId !== sessionId) state.requestInFlight = undefined;
  state.sessionId = sessionId;
};

const currentTurnReport = (state: IAlphaPodState): IAlphaVoiceResult | undefined =>
  state.voice.turnReportId === undefined
    ? undefined
    : state.voice.results?.find(
        result => result.kind === 'report' && result.id === state.voice.turnReportId,
      );

const appendVoiceResult = (
  state: IAlphaPodState,
  result: Omit<IAlphaVoiceResult, 'id'>,
): number => {
  const results = state.voice.results ?? [];
  const id = results.length ? results[results.length - 1].id + 1 : 1;
  state.voice.results = [...results, { id, ...result }];
  return id;
};

const withoutCardError = (state: IAlphaPodState, token: string): void => {
  if (!state.cardErrors || !(token in state.cardErrors)) return;
  state.cardErrors = Object.fromEntries(
    Object.entries(state.cardErrors).filter(([key]) => key !== token),
  );
};

const CANCELLED_TEXT = 'Cancelled. Nothing was changed.';
const SKIPPED_TEXT = 'You skipped this question.';

export interface CancelledNote {
  skipped?: boolean;
  message?: string;
}

const openCallFor = (
  calls: IAlphaToolCallStatus[] | undefined,
  result: { toolName: string; toolUseId?: string },
): IAlphaToolCallStatus | undefined => {
  const open = (calls ?? []).filter(call => call.ok === undefined);
  return (
    open.find(call => result.toolUseId && call.toolUseId === result.toolUseId) ??
    [...open].reverse().find(call => call.toolName === result.toolName)
  );
};

const VOICE_STAGE_STATUSES: AlphaVoiceStatus[] = [
  'connecting',
  'listening',
  'thinking',
  'speaking',
];

const initialVoiceState: IAlphaPodState['voice'] = {
  status: 'idle',
  error: undefined,
  liveItems: [],
};

const initialState: IAlphaPodState = {
  contextKey: undefined,
  sessionId: undefined,
  resolvedContext: undefined,
  creatingSession: false,
  sessionError: undefined,
  messages: [],
  pendingConfirmations: [],
  cardErrors: {},
  sessionsForContext: [],
  loadingSessions: false,
  historyOpen: false,
  rateLimitNotice: undefined,
  turnInProgressNotice: false,
  loadingSelectedSession: false,
  pendingAutoSummarize: false,
  voice: initialVoiceState,
};

const endStreaming = (state: IAlphaPodState): void => {
  state.messages.forEach(message => {
    const live = message.turnId !== undefined && message.turnId === state.requestInFlight?.id;
    if (message.streaming && !live) message.streaming = false;
  });
};

const clearStaleStreamingFlag = (state: IAlphaPodState): void => {
  for (let i = state.messages.length - 1; i >= 0; i -= 1) {
    const message = state.messages[i];
    if (message.role === 'assistant') {
      const live = message.turnId !== undefined && message.turnId === state.requestInFlight?.id;
      if (message.streaming && !live) message.streaming = false;
      break;
    }
  }
};

const lastAssistant = (state: IAlphaPodState): IAlphaMessage | undefined => {
  const live = state.requestInFlight?.id;
  for (let i = state.messages.length - 1; i >= 0; i -= 1) {
    const message = state.messages[i];
    if (message.turnId === undefined || message.turnId !== live) {
      return message.role === 'assistant' ? message : undefined;
    }
  }
  return undefined;
};

const isEmptyReply = (message: IAlphaMessage): boolean =>
  message.role === 'assistant' &&
  !message.text &&
  !message.toolCalls?.length &&
  !message.visuals?.length &&
  !message.needsInput &&
  !message.handoff;

const tagLocalMessages = (state: IAlphaPodState, tag: string): void => {
  const live = state.requestInFlight?.id;
  state.messages.forEach((message, position) => {
    if (!isLocalOnly(message) || message.localNotice) return;
    if (live !== undefined && message.turnId === live) return;
    message.localId = `${tag}-${position}`;
  });
};

const upsertPendingCard = (state: IAlphaPodState, card: IAlphaPendingConfirmation): void => {
  state.resolvedTokens = (state.resolvedTokens ?? []).filter(t => t !== card.token);
  const index = state.pendingConfirmations.findIndex(pending => pending.token === card.token);
  if (index >= 0) {
    state.pendingConfirmations[index] = card;
  } else {
    state.pendingConfirmations.push(card);
  }
};

const ownReply = (state: IAlphaPodState, turnId: number): IAlphaMessage | undefined =>
  state.messages.find(message => message.role === 'assistant' && message.turnId === turnId);

const turnMessage = (
  state: IAlphaPodState,
  turnId: number | undefined,
): IAlphaMessage | undefined => {
  if (turnId === undefined) return lastAssistant(state);
  const found = ownReply(state, turnId);
  if (found) return found;
  state.messages.push({
    role: 'assistant',
    text: '',
    streaming: true,
    toolCalls: [],
    fromConfirm: true,
    turnId,
  });
  return state.messages[state.messages.length - 1];
};

const withTurn = <P>(payload: P, turnId?: number): { payload: P; meta: number | undefined } => ({
  payload,
  meta: turnId,
});

const alphaPodSlice = createSlice({
  name: 'alphaPod',
  initialState,
  reducers: {
    resetForContext: (state, action: PayloadAction<string | undefined>) => {
      if (state.contextKey === action.payload) {
        return state;
      }
      return {
        ...initialState,
        contextKey: action.payload,
        historyOpen: state.historyOpen,
        selection: nextSelection(state),
      };
    },
    toggleHistory: state => {
      state.historyOpen = !state.historyOpen;
    },
    closeHistory: state => {
      state.historyOpen = false;
    },
    startNewChat: state => {
      changeSession(state, undefined);
      clearCardMarkers(state);
      state.resolvedContext = undefined;
      state.messages = [];
      state.pendingConfirmations = [];
      state.cardErrors = {};
      state.voiceReloadFailedSessionId = undefined;
      state.selection = nextSelection(state);
      state.sessionError = undefined;
      state.historyOpen = false;
      state.selectRequestId = undefined;
      state.pendingRestoreId = undefined;
      state.failedRestoreId = undefined;
      state.loadingSelectedSession = false;
      state.switchingChat = false;
      state.switchFailedSessionId = undefined;
      state.cardsUnreadSessionId = undefined;
      state.resolvedTokens = [];
    },
    userMessageAdded: {
      reducer: (state, action: PayloadAction<string, string, number | undefined>) => {
        clearStaleStreamingFlag(state);
        state.resolvedTokens = [
          ...(state.resolvedTokens ?? []),
          ...state.pendingConfirmations.map(confirmation => confirmation.token),
        ];
        state.pendingConfirmations = [];
        state.cardErrors = {};
        clearCardMarkers(state);
        state.switchFailedSessionId = undefined;
        state.sendCount = (state.sendCount ?? 0) + 1;
        const turn = action.meta === undefined ? {} : { turnId: action.meta };
        state.messages.push({ role: 'user', text: action.payload, ...turn });
        state.messages.push({
          role: 'assistant',
          text: '',
          streaming: true,
          toolCalls: [],
          ...turn,
        });
      },
      prepare: (text: string, turnId?: number) => withTurn(text, turnId),
    },
    assistantTurnStarted: (state, action: PayloadAction<number | undefined>) => {
      clearStaleStreamingFlag(state);
      state.messages.push({
        role: 'assistant',
        text: '',
        streaming: true,
        toolCalls: [],
        fromConfirm: true,
        ...(action.payload === undefined ? {} : { turnId: action.payload }),
      });
    },
    confirmPlaceholderRemoved: (state, action: PayloadAction<number>) => {
      const index = state.messages.findIndex(
        message => message.role === 'assistant' && message.turnId === action.payload,
      );
      if (index < 0) return;
      const placeholder = state.messages[index];
      if (isEmptyReply(placeholder)) {
        state.messages.splice(index, 1);
        return;
      }
      placeholder.streaming = false;
    },
    toolCallAdded: {
      reducer: (
        state,
        action: PayloadAction<
          {
            toolName: string;
            startedAt?: number;
            label?: string;
            runningLabel?: string;
            toolUseId?: string;
          },
          string,
          number | undefined
        >,
      ) => {
        const target = turnMessage(state, action.meta);
        if (target) {
          const { toolName, startedAt, label, runningLabel, toolUseId } = action.payload;
          target.toolCalls = [
            ...(target.toolCalls ?? []),
            {
              toolName,
              startedAt,
              ...(label ? { label } : {}),
              ...(runningLabel ? { runningLabel } : {}),
              ...(toolUseId ? { toolUseId } : {}),
            },
          ];
        }
      },
      prepare: (
        payload: { toolName: string; startedAt?: number; label?: string; toolUseId?: string },
        turnId?: number,
      ) => withTurn(payload, turnId),
    },
    toolResultResolved: {
      reducer: (
        state,
        action: PayloadAction<
          { toolName: string; ok: boolean; skipped?: boolean; label?: string; toolUseId?: string },
          string,
          number | undefined
        >,
      ) => {
        const target =
          action.meta === undefined
            ? state.messages[state.messages.length - 1]
            : ownReply(state, action.meta);
        const call = openCallFor(target?.toolCalls, action.payload);
        if (!call) return;
        call.ok = action.payload.ok;
        if (action.payload.skipped) call.skipped = true;
        if (action.payload.label && (action.payload.ok || !call.label)) {
          call.label = action.payload.label;
        }
      },
      prepare: (
        payload: {
          toolName: string;
          ok: boolean;
          skipped?: boolean;
          label?: string;
          toolUseId?: string;
        },
        turnId?: number,
      ) => withTurn(payload, turnId),
    },
    textDeltaAppended: {
      reducer: (state, action: PayloadAction<string, string, number | undefined>) => {
        const target = turnMessage(state, action.meta);
        if (target) target.text += action.payload;
      },
      prepare: (text: string, turnId?: number) => withTurn(text, turnId),
    },
    confirmationAdded: (state, action: PayloadAction<IAlphaPendingConfirmation>) => {
      state.resolvedTokens = (state.resolvedTokens ?? []).filter(t => t !== action.payload.token);
      state.pendingConfirmations.push(action.payload);
    },
    confirmationRemoved: (state, action: PayloadAction<string>) => {
      state.resolvedTokens = [...(state.resolvedTokens ?? []), action.payload];
      state.pendingConfirmations = state.pendingConfirmations.filter(
        c => c.token !== action.payload,
      );
      withoutCardError(state, action.payload);
      state.submittingTokens = withoutToken(state.submittingTokens, action.payload);
      state.cancellingTokens = withoutToken(state.cancellingTokens, action.payload);
    },
    cardRequired: {
      reducer: (state, action: PayloadAction<CardRequiredEvent, string, number | undefined>) => {
        upsertPendingCard(state, toPendingCard(action.payload));
        const target =
          action.meta === undefined ? lastAssistant(state) : ownReply(state, action.meta);
        if (action.payload.kind !== 'form' || !target) return;
        const opening = (target.toolCalls ?? [])
          .filter(call => call.toolName === action.payload.toolName)
          .pop();
        if (opening) opening.opensForm = true;
      },
      prepare: (event: CardRequiredEvent, turnId?: number) => withTurn(event, turnId),
    },
    cardReplaced: (state, action: PayloadAction<{ token: string; event: CardRequiredEvent }>) => {
      const { token, event } = action.payload;
      const next = toPendingCard(event);
      const index = state.pendingConfirmations.findIndex(pending => pending.token === token);
      if (index >= 0) {
        state.pendingConfirmations = state.pendingConfirmations
          .map((pending, position) => (position === index ? next : pending))
          .filter((pending, position) => position === index || pending.token !== next.token);
      } else if (!state.pendingConfirmations.some(pending => pending.token === next.token)) {
        state.pendingConfirmations.push(next);
      }
      if (token !== next.token) {
        state.resolvedTokens = [
          ...(state.resolvedTokens ?? []).filter(t => t !== next.token),
          token,
        ];
        state.submittingTokens = withoutToken(state.submittingTokens, token);
      }
      withoutCardError(state, token);
    },
    cardSubmitStarted: (state, action: PayloadAction<string>) => {
      withoutCardError(state, action.payload);
      const tokens = state.submittingTokens ?? [];
      if (!tokens.includes(action.payload)) state.submittingTokens = [...tokens, action.payload];
    },
    cardCancelStarted: (state, action: PayloadAction<string>) => {
      const tokens = state.cancellingTokens ?? [];
      if (!tokens.includes(action.payload)) state.cancellingTokens = [...tokens, action.payload];
    },
    cardCancelEnded: (state, action: PayloadAction<string>) => {
      state.cancellingTokens = withoutToken(state.cancellingTokens, action.payload);
    },
    requestStarted: (state, action: PayloadAction<IAlphaRequestInFlight>) => {
      state.requestInFlight = action.payload;
    },
    requestCardArrived: (state, action: PayloadAction<{ id: number; token: string }>) => {
      const request = state.requestInFlight;
      if (request?.id !== action.payload.id) return;
      const cards = request.cards ?? [];
      if (!cards.includes(action.payload.token)) request.cards = [...cards, action.payload.token];
    },
    requestFinished: (state, action: PayloadAction<number>) => {
      if (state.requestInFlight?.id === action.payload) state.requestInFlight = undefined;
    },
    cardSubmitEnded: (state, action: PayloadAction<string>) => {
      if (!state.submittingTokens?.includes(action.payload)) return;
      state.submittingTokens = state.submittingTokens.filter(token => token !== action.payload);
    },
    voiceReloadFailed: (state, action: PayloadAction<number>) => {
      state.voiceReloadFailedSessionId = action.payload;
    },
    cardErrorRecorded: (
      state,
      action: PayloadAction<{ token: string; error: CardServerError }>,
    ) => {
      if (!state.cardErrors) state.cardErrors = {};
      state.cardErrors[action.payload.token] = action.payload.error;
    },
    visualAdded: {
      reducer: (state, action: PayloadAction<unknown, string, number | undefined>) => {
        const visual = toVisual(action.payload);
        if (!visual) return;
        const target = turnMessage(state, action.meta);
        if (target) target.visuals = [...(target.visuals ?? []), visual];
      },
      prepare: (visual: unknown, turnId?: number) => withTurn(visual, turnId),
    },
    voiceVisualAdded: (state, action: PayloadAction<unknown>) => {
      const visual = toVisual(action.payload);
      if (!visual) return;
      const current = currentTurnReport(state);
      if (current) {
        current.visuals = [...(current.visuals ?? []), visual];
        return;
      }
      state.voice.turnReportId = appendVoiceResult(state, {
        kind: 'report',
        text: '',
        visuals: [visual],
      });
    },
    voiceTurnTextFlushed: (state, action: PayloadAction<{ text: string; report: boolean }>) => {
      const text = action.payload.text.trim();
      if (!text) return;
      const current = currentTurnReport(state);
      if (current) {
        current.text = current.text ? `${current.text}\n\n${text}` : text;
        return;
      }
      if (!action.payload.report) return;
      state.voice.turnReportId = appendVoiceResult(state, { kind: 'report', text });
    },
    voiceTurnEnded: state => {
      state.voice.turnReportId = undefined;
    },
    cancelledMessageAdded: (state, action: PayloadAction<CancelledNote | undefined>) => {
      clearStaleStreamingFlag(state);
      const note = action.payload;
      const text =
        note?.message?.trim() || (note?.skipped === true ? SKIPPED_TEXT : CANCELLED_TEXT);
      state.messages.push({
        role: 'assistant',
        text,
        streaming: false,
        toolCalls: [],
      });
      if (VOICE_STAGE_STATUSES.includes(state.voice.status)) {
        state.voice.liveItems.push({
          itemId: `${CANCELLED_ITEM_PREFIX}${state.voice.liveItems.length}`,
          role: 'assistant',
          text,
          final: true,
        });
      }
    },
    assistantNoteAdded: (state, action: PayloadAction<string>) => {
      clearStaleStreamingFlag(state);
      state.messages.push({
        role: 'assistant',
        text: action.payload,
        streaming: false,
        toolCalls: [],
      });
    },
    cancelFailedMessageAdded: state => {
      clearStaleStreamingFlag(state);
      state.messages.push({
        role: 'assistant',
        text: "Couldn't cancel that action. It is still waiting for you, so try Cancel again.",
        streaming: false,
        toolCalls: [],
      });
    },
    messageStreamEnded: {
      reducer: (state, action: PayloadAction<undefined, string, number | undefined>) => {
        if (action.meta === undefined) {
          endStreaming(state);
          return;
        }
        const target = ownReply(state, action.meta);
        if (target) target.streaming = false;
      },
      prepare: (turnId?: number) => withTurn(undefined, turnId),
    },
    streamErrorOccurred: {
      reducer: (state, action: PayloadAction<string, string, number | undefined>) => {
        if (action.meta === undefined) {
          endStreaming(state);
          state.messages.push({
            role: 'assistant',
            text: action.payload,
            streaming: false,
            toolCalls: [],
          });
          return;
        }
        const target = turnMessage(state, action.meta);
        if (!target) return;
        target.streaming = false;
        if (!target.text) {
          target.text = action.payload;
          return;
        }
        const index = state.messages.indexOf(target);
        state.messages.splice(index + 1, 0, {
          role: 'assistant',
          text: action.payload,
          streaming: false,
          toolCalls: [],
        });
      },
      prepare: (message: string, turnId?: number) => withTurn(message, turnId),
    },
    confirmNoticeAdded: (state, action: PayloadAction<string>) => {
      clearStaleStreamingFlag(state);
      state.messages.push({
        role: 'assistant',
        text: action.payload,
        streaming: false,
        toolCalls: [],
        fromConfirm: true,
        localNotice: true,
        ...noticeAnchor(state.messages),
      });
    },
    chatSwitchStarted: state => {
      state.switchingChat = true;
      state.switchFailedSessionId = undefined;
      state.cardsUnreadSessionId = undefined;
    },
    chatSwitchEnded: state => {
      state.switchingChat = false;
      state.cardsUnreadSessionId = undefined;
    },
    chatSwitchFailed: (state, action: PayloadAction<number>) => {
      const owed = state.pendingRestoreId !== undefined && state.sessionId !== undefined;
      const failed = owed && state.failedRestoreId === state.pendingRestoreId;
      state.switchingChat = owed;
      state.cardsUnreadSessionId = failed ? state.sessionId : undefined;
      state.switchFailedSessionId = action.payload;
    },
    chatCardsUnread: (state, action: PayloadAction<number>) => {
      state.cardsUnreadSessionId = action.payload;
    },
    chatCardsReadRetried: state => {
      state.cardsUnreadSessionId = undefined;
    },
    chatCardsReadDismissed: state => {
      state.cardsUnreadSessionId = undefined;
      state.pendingRestoreId = undefined;
      state.failedRestoreId = undefined;
      state.switchingChat = false;
    },
    exchangeRolledBack: (state, action: PayloadAction<number>) => {
      state.messages = state.messages.filter(message => message.turnId !== action.payload);
      state.sendCount = Math.max(0, (state.sendCount ?? 0) - 1);
    },
    streamsStopped: (state, action: PayloadAction<number>) => {
      const older = (message: IAlphaMessage): boolean =>
        message.turnId === undefined || message.turnId <= action.payload;
      state.messages = state.messages.filter(
        message => !(older(message) && message.streaming && isEmptyReply(message)),
      );
      state.messages.forEach(message => {
        if (older(message) && message.streaming) message.streaming = false;
      });
    },
    voiceCardArrived: (
      state,
      action: PayloadAction<CardRequiredEvent | ConfirmationRequiredEvent>,
    ) => {
      const event = action.payload;
      upsertPendingCard(
        state,
        event.type === 'card_required'
          ? toPendingCard(event)
          : {
              toolName: event.toolName,
              preview: event.preview,
              token: event.token,
              reviewUrl: event.reviewUrl,
              ...(event.title ? { title: event.title } : {}),
            },
      );
    },
    noticesCleared: state => {
      state.rateLimitNotice = undefined;
      state.turnInProgressNotice = false;
    },
    rateLimitNoticed: (
      state,
      action: PayloadAction<{ message?: string; retryAfterSeconds: number }>,
    ) => {
      state.rateLimitNotice = {
        message: action.payload.message || "You're sending messages quickly.",
        retryAfterSeconds: action.payload.retryAfterSeconds,
        retryAt: Date.now() + action.payload.retryAfterSeconds * 1000,
      };
    },
    turnInProgressNoticed: state => {
      state.turnInProgressNotice = true;
    },
    autoSummarizeRequested: state => {
      state.pendingAutoSummarize = true;
    },
    autoSummarizeConsumed: state => {
      state.pendingAutoSummarize = false;
    },
    needsInputAdded: {
      reducer: (
        state,
        action: PayloadAction<IAlphaMessage['needsInput'], string, number | undefined>,
      ) => {
        const target = turnMessage(state, action.meta);
        if (target) target.needsInput = action.payload;
      },
      prepare: (needsInput: IAlphaMessage['needsInput'], turnId?: number) =>
        withTurn(needsInput, turnId),
    },
    needsInputResolved: state => {
      const last = state.messages[state.messages.length - 1];
      if (last?.needsInput) last.needsInput = undefined;
    },
    handoffAdded: {
      reducer: (
        state,
        action: PayloadAction<IAlphaMessage['handoff'], string, number | undefined>,
      ) => {
        const target = turnMessage(state, action.meta);
        if (target) target.handoff = action.payload;
      },
      prepare: (handoff: IAlphaMessage['handoff'], turnId?: number) => withTurn(handoff, turnId),
    },
    voiceStarted: state => {
      state.voice = {
        turnReportId: undefined,
        status: 'connecting',
        error: undefined,
        errorCode: undefined,
        results: [],
        activity: undefined,
        liveItems: [],
      };
    },
    voiceStatusChanged: (
      state,
      action: PayloadAction<{
        status: AlphaVoiceStatus;
        error?: string;
        errorCode?: VoiceErrorCode;
      }>,
    ) => {
      state.voice.status = action.payload.status;
      state.voice.error = action.payload.error;
      state.voice.errorCode = action.payload.errorCode;
      if (action.payload.status === 'ended') state.voice.activity = undefined;
    },
    voiceActivityChanged: (state, action: PayloadAction<string | undefined>) => {
      state.voice.activity = action.payload;
    },
    voiceResultAdded: (state, action: PayloadAction<Omit<IAlphaVoiceResult, 'id'>>) => {
      appendVoiceResult(state, action.payload);
    },
    popupContextReceived: (state, action: PayloadAction<IAlphaResolvedContextResult>) => {
      state.resolvedContext = action.payload;
    },
    voiceTranscriptEventApplied: (state, action: PayloadAction<IOaiRealtimeEvent>) => {
      state.voice.liveItems = applyVoiceTranscriptEvent(state.voice.liveItems, action.payload);
    },
    voiceEnded: state => {
      state.voice = {
        turnReportId: undefined,
        status: 'idle',
        error: undefined,
        errorCode: undefined,
        results: [],
        activity: undefined,
        liveItems: [],
      };
      state.messages = state.messages.filter(
        message =>
          !(
            message.role === 'assistant' &&
            message.streaming &&
            !message.text &&
            !(message.toolCalls && message.toolCalls.length > 0) &&
            !(message.visuals && message.visuals.length > 0)
          ),
      );
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(ThunkSelectAlphaSession.pending, (state, action) => {
      state.loadingSelectedSession = true;
      state.selectRequestId = action.meta.requestId;
    });
    addCase(ThunkSelectAlphaSession.fulfilled, (state, action) => {
      if (state.selectRequestId !== action.meta.requestId) return;
      state.loadingSelectedSession = false;
      state.selectRequestId = undefined;
      state.switchFailedSessionId = undefined;
      state.retriedTokens =
        state.sessionId === action.payload.sessionId
          ? state.pendingConfirmations.filter(card => card.retried).map(card => card.token)
          : [];
      changeSession(state, action.payload.sessionId);
      state.messages = action.payload.messages;
      state.pendingConfirmations = [];
      state.cardErrors = {};
      clearCardMarkers(state);
      state.voiceReloadFailedSessionId = undefined;
      state.selection = nextSelection(state);
      state.pendingRestoreId = action.meta.requestId;
      state.failedRestoreId = undefined;
      state.resolvedTokens = [];
      state.sessionError = undefined;
      state.rateLimitNotice = undefined;
      state.turnInProgressNotice = false;
    });
    addCase(ThunkRefreshAfterVoice.pending, (state, action) => {
      tagLocalMessages(state, action.meta.requestId);
    });
    addCase(ThunkRefreshAfterVoice.fulfilled, (state, action) => {
      const {
        sessionId,
        messages,
        seenAtStart,
        applyMessages,
        sentDuringRefresh,
        shownAtStart,
        servedTokens,
        pending,
        imported,
      } = action.payload;
      if (state.sessionId !== sessionId) return;
      const resolved = new Set(state.resolvedTokens ?? []);
      const live = pending.filter(card => !resolved.has(card.token));
      const liveTokens = new Set(live.map(card => card.token));
      state.messages = clearBackedExpiryNotices(
        applyMessages ? mergeReload(messages, seenAtStart, state.messages) : state.messages,
        live,
      );
      const retried = new Set(
        state.pendingConfirmations.filter(card => card.retried).map(card => card.token),
      );
      const withFlag = (card: IAlphaPendingConfirmation): IAlphaPendingConfirmation =>
        retried.has(card.token) ? { ...card, retried: true } : card;
      if (applyMessages) {
        state.pendingConfirmations = live.map(withFlag);
        state.cardErrors = Object.fromEntries(
          Object.entries(state.cardErrors ?? {}).filter(([token]) => liveTokens.has(token)),
        );
      } else if (!sentDuringRefresh) {
        const served = new Set(servedTokens);
        const wasShown = new Set(shownAtStart);
        state.pendingConfirmations = state.pendingConfirmations.filter(
          card => !wasShown.has(card.token) || served.has(card.token),
        );
        const shown = new Set(state.pendingConfirmations.map(card => card.token));
        state.pendingConfirmations.push(...live.filter(card => !shown.has(card.token)));
      }
      if (imported.length > 0) {
        const revisions = { ...(state.draftImports ?? {}) };
        imported.forEach(token => {
          revisions[token] = (revisions[token] ?? 0) + 1;
        });
        state.draftImports = revisions;
      }
      if (applyMessages && state.voiceReloadFailedSessionId === sessionId) {
        state.voiceReloadFailedSessionId = undefined;
      }
    });
    addCase(ThunkRestorePendingConfirmations.pending, (state, action) => {
      if (state.pendingRestoreId === action.meta.arg.restoreId) {
        state.failedRestoreId = undefined;
      }
    });
    addCase(ThunkRestorePendingConfirmations.rejected, (state, action) => {
      const { sessionId, restoreId } = action.meta.arg;
      if (state.sessionId !== sessionId || state.pendingRestoreId !== restoreId) return;
      state.failedRestoreId = restoreId;
      if (state.switchingChat && !state.loadingSelectedSession) {
        state.cardsUnreadSessionId = sessionId;
      }
    });
    addCase(ThunkRestorePendingConfirmations.fulfilled, (state, action) => {
      const { sessionId, restoreId } = action.meta.arg;
      if (state.sessionId !== sessionId) return;
      if (state.pendingRestoreId !== restoreId) return;
      state.pendingRestoreId = undefined;
      state.failedRestoreId = undefined;
      if (state.switchingChat && !state.loadingSelectedSession) {
        state.switchingChat = false;
        state.cardsUnreadSessionId = undefined;
      }
      const skip = new Set([
        ...state.pendingConfirmations.map(c => c.token),
        ...(state.resolvedTokens ?? []),
      ]);
      const retried = new Set(state.retriedTokens ?? []);
      state.retriedTokens = undefined;
      const added = action.payload
        .filter(c => !skip.has(c.token))
        .map(c => (retried.has(c.token) ? { ...c, retried: true } : c));
      if (added.length === 0) return;
      state.pendingConfirmations.push(...added);
      state.messages = clearBackedExpiryNotices(state.messages, added);
    });
    addCase(ThunkReloadAlphaMessages.pending, (state, action) => {
      tagLocalMessages(state, action.meta.requestId);
    });
    addCase(ThunkReloadAlphaMessages.fulfilled, (state, action) => {
      const { sessionId, messages, seenAtStart, apply } = action.payload;
      if (!apply || state.sessionId !== sessionId) return;
      state.messages = mergeReload(messages, seenAtStart, state.messages);
      if (state.voiceReloadFailedSessionId === sessionId) {
        state.voiceReloadFailedSessionId = undefined;
      }
    });
    addCase(ThunkSelectAlphaSession.rejected, (state, action) => {
      if (state.selectRequestId !== action.meta.requestId) return;
      state.loadingSelectedSession = false;
      state.selectRequestId = undefined;
    });
    addCase(ThunkCreateAlphaSession.pending, (state, action) => {
      state.creatingSession = true;
      state.createRequestId = action.meta.requestId;
      state.sessionError = undefined;
      state.sessionErrorUnreachable = false;
      state.sessionErrorSourceDisabled = false;
    });
    addCase(ThunkCreateAlphaSession.fulfilled, (state, action) => {
      if (state.createRequestId !== action.meta.requestId) return;
      state.creatingSession = false;
      state.createRequestId = undefined;
      if (state.sessionId !== action.payload.id) {
        state.pendingRestoreId = undefined;
        state.failedRestoreId = undefined;
        if (!state.loadingSelectedSession) {
          state.cardsUnreadSessionId = undefined;
          state.switchingChat = false;
        }
      }
      changeSession(state, action.payload.id);
      state.resolvedContext = action.payload.resolvedContext;
      state.selection = nextSelection(state);
    });
    addCase(ThunkCreateAlphaSession.rejected, (state, action) => {
      if (state.createRequestId !== action.meta.requestId) return;
      state.creatingSession = false;
      state.createRequestId = undefined;
      state.sessionError =
        action.payload?.message ?? "Couldn't open Alpha for this ticket. Try again.";
      state.sessionErrorUnreachable = action.payload?.unreachable === true;
      state.sessionErrorSourceDisabled = action.payload?.sourceDisabled === true;
    });
    addCase(ThunkGetAlphaSessionsForContext.pending, state => {
      state.loadingSessions = true;
    });
    addCase(ThunkGetAlphaSessionsForContext.fulfilled, (state, action) => {
      state.loadingSessions = false;
      state.sessionsForContext = action.payload;
    });
    addCase(ThunkGetAlphaSessionsForContext.rejected, state => {
      state.loadingSessions = false;
    });
  },
});

export default alphaPodSlice.reducer;
export const {
  resetForContext,
  toggleHistory,
  closeHistory,
  startNewChat,
  userMessageAdded,
  assistantTurnStarted,
  confirmPlaceholderRemoved,
  toolCallAdded,
  toolResultResolved,
  textDeltaAppended,
  confirmationAdded,
  confirmationRemoved,
  cardRequired,
  cardReplaced,
  cardErrorRecorded,
  cardSubmitStarted,
  cardSubmitEnded,
  cardCancelStarted,
  cardCancelEnded,
  requestStarted,
  requestCardArrived,
  requestFinished,
  voiceReloadFailed,
  visualAdded,
  voiceVisualAdded,
  voiceTurnTextFlushed,
  voiceTurnEnded,
  cancelledMessageAdded,
  assistantNoteAdded,
  cancelFailedMessageAdded,
  messageStreamEnded,
  streamErrorOccurred,
  exchangeRolledBack,
  confirmNoticeAdded,
  chatSwitchStarted,
  chatSwitchEnded,
  chatSwitchFailed,
  chatCardsUnread,
  chatCardsReadRetried,
  chatCardsReadDismissed,
  streamsStopped,
  voiceCardArrived,
  noticesCleared,
  rateLimitNoticed,
  turnInProgressNoticed,
  autoSummarizeRequested,
  autoSummarizeConsumed,
  needsInputAdded,
  needsInputResolved,
  handoffAdded,
  voiceStarted,
  voiceStatusChanged,
  voiceResultAdded,
  voiceActivityChanged,
  popupContextReceived,
  voiceTranscriptEventApplied,
  voiceEnded,
} = alphaPodSlice.actions;

export const AlphaPodSelector = (state: RootState): IAlphaPodState => state.alphaPod;
